/**
 * 📅 EventSchedulerPage — owner-only one-page console that drives the
 * three timed arcade events on OmniMuscle:
 *
 *   • Phoenix Summit  — 36-tile festival board
 *   • Jewel Hunt      — 60-second match-3 mini-game
 *   • Fireworks       — July 4th arcade board
 *
 * Each row shows:
 *   • A green/red LIVE badge for the current state
 *   • The scheduled window (ISO strings → readable "Mon Jul 1 • 2:00 PM")
 *   • Instant toggle (on now, off now, set a window)
 *   • A SAVE button to commit a date-range window
 *
 * No React Router dynamic imports — this is a single lightweight page
 * that imports from the shared AuthContext and talks to:
 *   GET  /api/admin/event-schedule        → list all 3 schedules
 *   POST /api/admin/event-schedule        → upsert one event's window
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Convert an ISO 8601 UTC string to a `<input type="datetime-local">`
 * compatible local-time string (YYYY-MM-DDTHH:mm) in the browser's TZ.
 * Returns "" for null/undefined/invalid. */
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Convert a `<input type="datetime-local">` value back to a UTC ISO
 *  string. Returns null for empty input. */
function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Render a human-friendly "Mon Jul 1 • 2:00 PM" from an ISO string. */
function prettyIso(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

/** Build an ISO window for a known holiday, anchored to the current
 *  or next upcoming calendar occurrence (whichever is sooner). Returns
 *  { start_iso, until_iso, label }. */
function holidayWindow(key) {
  const now = new Date();
  const y   = now.getFullYear();
  const mk  = (year, m, d, h = 0) =>
    new Date(Date.UTC(year, m, d, h, 0, 0)).toISOString();
  switch (key) {
    case "july_4_week": {
      // July 1 (midnight UTC) → July 7 23:59 UTC. If we're past July 7
      // already, roll forward to next year.
      const nextY = (now.getMonth() > 6 || (now.getMonth() === 6 && now.getDate() > 7)) ? y + 1 : y;
      return {
        start_iso: mk(nextY, 6, 1, 0),
        until_iso: mk(nextY, 6, 7, 23),
        label:     `🎆 July 4th Week ${nextY}`,
      };
    }
    case "halloween_weekend": {
      // Oct 30 → Nov 1 (midnight UTC → midnight UTC). Roll forward if past Nov 1.
      const nextY = (now.getMonth() > 10 || (now.getMonth() === 10 && now.getDate() > 1)) ? y + 1 : y;
      return {
        start_iso: mk(nextY, 9, 30, 0),
        until_iso: mk(nextY, 10, 1, 23),
        label:     `🎃 Halloween Weekend ${nextY}`,
      };
    }
    case "weekend": {
      // Current or upcoming Fri 6 PM local → Sun 11:59 PM local.
      const d = new Date();
      const day = d.getDay(); // 0 Sun … 6 Sat
      const offsetToFri = day <= 5 ? (5 - day) : (12 - day); // next Fri
      const fri = new Date(d);
      fri.setDate(d.getDate() + offsetToFri);
      fri.setHours(18, 0, 0, 0);
      const sun = new Date(fri);
      sun.setDate(fri.getDate() + 2);
      sun.setHours(23, 59, 0, 0);
      return {
        start_iso: fri.toISOString(),
        until_iso: sun.toISOString(),
        label:     "🎉 Weekend",
      };
    }
    default:
      return null;
  }
}


export default function EventSchedulerPage() {
  const { user, loading } = useAuth();
  const isOwner = user && (user.role === "owner" || (user.email || "").toLowerCase() === "omnimuscle@yahoo.com");
  const [schedules, setSchedules] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  // Local draft state — keyed by event_key — so the owner can edit the
  // date window without hitting the server on every keystroke.
  const [drafts, setDrafts] = useState({});

  const refresh = async () => {
    setFetching(true);
    try {
      const { data } = await axios.get(`${API}/admin/event-schedule`, { withCredentials: true });
      setSchedules(data.schedules || []);
      // Seed drafts from the fresh server state
      const seed = {};
      (data.schedules || []).forEach((s) => {
        seed[s.key] = {
          start_local: isoToLocalInput(s.start_iso),
          until_local: isoToLocalInput(s.until_iso),
          label:       s.label || "",
        };
      });
      setDrafts(seed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load schedules.");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    document.title = "📅 Event Scheduler · Admin · OMNIMUSCLE";
    if (isOwner) refresh();
  }, [isOwner]);

  const save = async (event_key, overrides = {}) => {
    const draft = drafts[event_key] || {};
    const body = {
      event_key,
      active:     overrides.active !== undefined ? overrides.active : true,
      label:      overrides.label  !== undefined ? overrides.label  : (draft.label || ""),
      start_iso:  overrides.start_iso !== undefined ? overrides.start_iso : localInputToIso(draft.start_local),
      until_iso:  overrides.until_iso !== undefined ? overrides.until_iso : localInputToIso(draft.until_local),
    };
    setSavingKey(event_key);
    try {
      await axios.post(`${API}/admin/event-schedule`, body, { withCredentials: true });
      toast.success(body.active ? `✅ ${event_key} window saved.` : `🪦 ${event_key} turned OFF.`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Save failed for ${event_key}.`);
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) return null;
  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400"
           data-testid="event-scheduler-locked">
        🔒 Owner only.
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-8" data-testid="event-scheduler-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl mb-1">📅 Event Scheduler</h1>
        <p className="text-xs text-neutral-500">
          Flip Phoenix Summit · Jewel Hunt · Fireworks on and off at will. Date windows
          auto-fire inside their range; "Turn OFF now" is a kill-switch.
        </p>
      </div>

      {fetching && <div className="text-neutral-400 text-sm">Loading…</div>}

      <div className="space-y-4">
        {schedules.map((s) => {
          const d = drafts[s.key] || {};
          const saving = savingKey === s.key;
          return (
            <div
              key={s.key}
              data-testid={`event-row-${s.key}`}
              className={`border-2 p-5 bg-[#0F0F0F] ${s.live_now ? "border-emerald-600" : "border-neutral-800"}`}
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="text-3xl" aria-hidden>{s.icon}</div>
                  <div>
                    <h2 className="font-display text-xl text-white">{s.name}</h2>
                    <div className="text-[11px] text-neutral-500">{s.blurb}</div>
                  </div>
                </div>
                <div
                  data-testid={`event-live-${s.key}`}
                  className={
                    "text-[10px] font-black uppercase tracking-widest px-2 py-1 border " +
                    (s.live_now
                      ? "border-emerald-500 text-emerald-300 bg-emerald-900/30"
                      : "border-neutral-700 text-neutral-500")
                  }
                >
                  {s.live_now ? "● LIVE" : "○ Off"}
                </div>
              </div>

              {/* Current server state summary */}
              <div className="mb-3 grid grid-cols-2 gap-2 text-[11px] text-neutral-400 bg-black/30 border border-neutral-800 p-2.5">
                <div><span className="text-neutral-600">Active flag:</span> <span className="text-neutral-200">{s.active ? "yes" : "no"}</span></div>
                <div><span className="text-neutral-600">Label:</span> <span className="text-neutral-200">{s.label || "—"}</span></div>
                <div><span className="text-neutral-600">Starts:</span> <span className="text-neutral-200">{prettyIso(s.start_iso)}</span></div>
                <div><span className="text-neutral-600">Ends:</span> <span className="text-neutral-200">{prettyIso(s.until_iso)}</span></div>
              </div>

              {/* 🎯 Feb 2026 — one-tap holiday presets. Each preset
                 computes a start/end window for the next occurrence and
                 instantly saves it as ACTIVE. Use "Weekend" for a
                 generic Fri-Sun run (local time). */}
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">
                  One-tap presets
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid={`event-preset-july4-${s.key}`}
                    disabled={saving}
                    onClick={() => {
                      const w = holidayWindow("july_4_week");
                      save(s.key, { active: true, start_iso: w.start_iso, until_iso: w.until_iso, label: w.label });
                    }}
                    className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-sky-700 text-sky-200 hover:bg-sky-900/30 disabled:text-neutral-500 disabled:border-neutral-800"
                  >
                    🎆 July 4th Week
                  </button>
                  <button
                    data-testid={`event-preset-halloween-${s.key}`}
                    disabled={saving}
                    onClick={() => {
                      const w = holidayWindow("halloween_weekend");
                      save(s.key, { active: true, start_iso: w.start_iso, until_iso: w.until_iso, label: w.label });
                    }}
                    className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-orange-700 text-orange-200 hover:bg-orange-900/30 disabled:text-neutral-500 disabled:border-neutral-800"
                  >
                    🎃 Halloween Weekend
                  </button>
                  <button
                    data-testid={`event-preset-weekend-${s.key}`}
                    disabled={saving}
                    onClick={() => {
                      const w = holidayWindow("weekend");
                      save(s.key, { active: true, start_iso: w.start_iso, until_iso: w.until_iso, label: w.label });
                    }}
                    className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-violet-700 text-violet-200 hover:bg-violet-900/30 disabled:text-neutral-500 disabled:border-neutral-800"
                  >
                    🎉 This Weekend
                  </button>
                </div>
              </div>

              {/* Editable window */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <label className="text-[11px] text-neutral-500 flex flex-col gap-1">
                  Start (local time) — optional
                  <input
                    type="datetime-local"
                    data-testid={`event-start-${s.key}`}
                    value={d.start_local || ""}
                    onChange={(e) => setDrafts({ ...drafts, [s.key]: { ...d, start_local: e.target.value } })}
                    className="bg-black border border-neutral-800 px-2 py-1.5 text-neutral-100 text-xs focus:border-amber-400 focus:outline-none"
                  />
                </label>
                <label className="text-[11px] text-neutral-500 flex flex-col gap-1">
                  End (local time)
                  <input
                    type="datetime-local"
                    data-testid={`event-until-${s.key}`}
                    value={d.until_local || ""}
                    onChange={(e) => setDrafts({ ...drafts, [s.key]: { ...d, until_local: e.target.value } })}
                    className="bg-black border border-neutral-800 px-2 py-1.5 text-neutral-100 text-xs focus:border-amber-400 focus:outline-none"
                  />
                </label>
              </div>
              <label className="block mb-3 text-[11px] text-neutral-500">
                Window label (optional)
                <input
                  type="text"
                  data-testid={`event-label-${s.key}`}
                  value={d.label || ""}
                  placeholder={`${s.icon} ${s.name} (Owner-scheduled)`}
                  onChange={(e) => setDrafts({ ...drafts, [s.key]: { ...d, label: e.target.value } })}
                  className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1.5 text-neutral-100 text-xs focus:border-amber-400 focus:outline-none"
                />
              </label>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid={`event-save-${s.key}`}
                  disabled={saving}
                  onClick={() => save(s.key, { active: true })}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-amber-500 text-black hover:bg-amber-300 disabled:bg-neutral-800 disabled:text-neutral-500"
                >
                  {saving ? "Saving…" : "💾 Save Window"}
                </button>
                <button
                  data-testid={`event-live-now-${s.key}`}
                  disabled={saving}
                  onClick={() => {
                    // Instant go-live for 24h from now (no start, +1d end).
                    const until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
                    save(s.key, { active: true, start_iso: null, until_iso: until });
                  }}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest border border-emerald-700 text-emerald-200 hover:bg-emerald-900/30 disabled:text-neutral-500 disabled:border-neutral-800"
                >
                  ⚡ Turn ON for 24h
                </button>
                <button
                  data-testid={`event-off-${s.key}`}
                  disabled={saving}
                  onClick={() => save(s.key, { active: false, start_iso: null, until_iso: null })}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest border border-red-800 text-red-300 hover:bg-red-950/30 disabled:text-neutral-500 disabled:border-neutral-800"
                >
                  🪦 Turn OFF now
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {!fetching && schedules.length === 0 && (
        <div className="text-center text-neutral-500 text-sm py-10">
          No events registered. Check <code>event_scheduler_service.EVENT_KEYS</code>.
        </div>
      )}
    </div>
  );
}
